import { useCallback, useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { enqueueSnackbar } from '@mint-lib/notifications-api';
import { useQueryClient } from '@mint-lib/query-client';
import { KnowledgeBase } from '@mint-sdk/api-ai-assistant';

import {
  hasSiblingNamed,
  PENDING_FOLDER_ID_PREFIX,
} from '../../helpers/knowledge-base/fileSystemNode.js';
import { updateListedFolders } from '../../queries/knowledgeBase.js';
import { toApiFolderId, type FolderNode } from '../../types/fileSystem.js';

type Folders = KnowledgeBase.Folder[];

const appendFolder = (folder: KnowledgeBase.Folder) => (folders: Folders) => [
  ...folders,
  folder,
];

const replaceFolder =
  (folderId: string, folder: KnowledgeBase.Folder) => (folders: Folders) =>
    folders.map((listed) => (listed.folderId === folderId ? folder : listed));

const removeFolder = (folderId: string) => (folders: Folders) =>
  folders.filter((listed) => listed.folderId !== folderId);

const renameListed = (folderId: string, name: string) => (folders: Folders) =>
  folders.map((listed) =>
    listed.folderId === folderId ? { ...listed, name } : listed,
  );

type Params = {
  parentId: string;
  siblings: FolderNode[];
};

export const useCreateFolder = ({ parentId, siblings }: Params) => {
  const { t } = useTranslation('@mint-lib/ai-chat');
  const queryClient = useQueryClient();
  const { mutateAsync: create } = KnowledgeBase.createFolder();
  const { mutateAsync: update } = KnowledgeBase.updateFolder();

  const [isDrafting, setIsDrafting] = useState(false);

  const startDraft = useCallback(() => setIsDrafting(true), []);
  const cancelDraft = useCallback(() => setIsDrafting(false), []);

  const validateName = useCallback(
    (name: string, excludeId?: string) =>
      Boolean(name.trim()) && !hasSiblingNamed(siblings, name, excludeId),
    [siblings],
  );

  const commitDraft = useCallback(
    (name: string) => {
      if (!validateName(name)) return false;

      const pendingId = `${PENDING_FOLDER_ID_PREFIX}${crypto.randomUUID()}`;
      const data = {
        name: name.trim(),
        parentFolderId: toApiFolderId(parentId),
      };

      updateListedFolders(
        queryClient,
        parentId,
        appendFolder({ folderId: pendingId, ...data }),
        { cancelRefetch: true },
      );

      create({ data })
        .then((created) =>
          updateListedFolders(
            queryClient,
            parentId,
            replaceFolder(pendingId, created),
          ),
        )
        .catch(() => {
          updateListedFolders(queryClient, parentId, removeFolder(pendingId));

          enqueueSnackbar({
            variant: 'error',
            subTitle: t(
              'Something went wrong. The folder could not be created',
            ),
            removable: true,
          });
        });

      setIsDrafting(false);

      return true;
    },
    [validateName, create, parentId, queryClient, t],
  );

  const renameFolder = useCallback(
    (id: string, name: string) => {
      if (!validateName(name, id)) return false;

      const trimmed = name.trim();
      const previous = siblings.find((folder) => folder.id === id)?.name;

      updateListedFolders(queryClient, parentId, renameListed(id, trimmed), {
        cancelRefetch: true,
      });

      update({ urlParams: { folderId: id }, data: { name: trimmed } }).catch(
        () => {
          if (previous !== undefined) {
            updateListedFolders(
              queryClient,
              parentId,
              renameListed(id, previous),
            );
          }

          enqueueSnackbar({
            variant: 'error',
            subTitle: t(
              'Something went wrong. The folder could not be renamed',
            ),
            removable: true,
          });
        },
      );

      return true;
    },
    [validateName, update, siblings, parentId, queryClient, t],
  );

  return {
    isDrafting,
    startDraft,
    cancelDraft,
    commitDraft,
    renameFolder,
    validateName,
  };
};
